import { motion as motionFramer } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Lightbulb,
  Award,
  Handshake,
  Users,
  Search,
  Code2,
  Rocket,
  RefreshCcw,
  Brain,
  Server,
  BarChart3,
  Cloud,
  ArrowRight
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ClientMarquee from "@/components/ClientMarquee";
import { AnimatedSection, FadeIn, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO from "@/components/SEO";
const coreValues = [
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Pushing boundaries with cutting-edge AI solutions that redefine what's possible.",
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    badgeText: "Push Boundaries",
    tagLine: "Our Direction",
    tags: ["AI-First", "Research", "Future-proof"],
  },
  {
    icon: Award,
    title: "Excellence",
    description: "Committed to delivering production-ready systems that exceed expectations.",
    color: "bg-blue-50",
    iconColor: "text-[#243b7f]",
    badgeText: "Enterprise Quality",
    tagLine: "Our Standards",
    tags: ["Production-ready", "Scalable", "Robust"],
  },
  {
    icon: Handshake,
    title: "Trust",
    description: "Building lasting partnerships through transparency and reliability.",
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    badgeText: "True Partnership",
    tagLine: "Our Promise",
    tags: ["Transparency", "Reliability", "Integrity"],
  },
  {
    icon: Users,
    title: "Collaboration",
    description: "Working as an extension of your team to achieve shared goals.",
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    badgeText: "Shared Success",
    tagLine: "Our Teamwork",
    tags: ["Co-innovation", "Talent", "Alignment"],
  },
];

const howWeWork = [
  {
    icon: Search,
    step: "01",
    title: "Consulting-first approach",
    description: "We start by understanding your business challenges and objectives before proposing solutions.",
  },
  {
    icon: Code2,
    step: "02",
    title: "Full-stack development",
    description: "Our team builds complete solutions from frontend to backend, ensuring seamless integration.",
  },
  {
    icon: Rocket,
    step: "03",
    title: "Integration & deployment",
    description: "We deploy solutions that integrate smoothly with your existing infrastructure.",
  },
  {
    icon: RefreshCcw,
    step: "04",
    title: "Continuous improvement",
    description: "Ongoing optimization and support to ensure your solutions evolve with your needs.",
  },
];

const techStack = [
  {
    category: "AI & ML",
    icon: Brain,
    items: [
      "Custom model development",
      "Training & optimization",
      "LLMs & VLMs",
      (
        <span key="cv">
          <a href="https://www.ibm.com/topics/computer-vision" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Computer Vision</a>
        </span>
      )
    ],
    color: "border-l-[#243b7f]",
  },
  {
    category: "Full-Stack",
    icon: Server,
    items: [
      "Web + mobile apps",
      "APIs & backend systems",
      (
        <span key="react-flutter-node">
          <a href="https://react.dev/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">React</a>,{" "}
          <a href="https://flutter.dev/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Flutter</a>,{" "}
          <a href="https://nodejs.org/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Node.js</a>
        </span>
      ),
      (
        <span key="python-fastapi">
          <a href="https://www.python.org/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Python</a>,{" "}
          <a href="https://fastapi.tiangolo.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">FastAPI</a>
        </span>
      )
    ],
    color: "border-l-amber-500",
  },
  {
    category: "Data & Analytics",
    icon: BarChart3,
    items: [
      "BI dashboards",
      "Data pipelines",
      (
        <span key="bi-tools">
          <a href="https://powerbi.microsoft.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Power BI</a>,{" "}
          <a href="https://www.tableau.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Tableau</a>,{" "}
          <a href="https://cloud.google.com/looker" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Looker</a>
        </span>
      ),
      "ETL & Data Warehousing"
    ],
    color: "border-l-emerald-500",
  },
  {
    category: "Cloud & DevOps",
    icon: Cloud,
    items: [
      (
        <span key="cloud-providers">
          <a href="https://aws.amazon.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">AWS</a>,{" "}
          <a href="https://azure.microsoft.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Azure</a>,{" "}
          <a href="https://cloud.google.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">GCP</a>
        </span>
      ),
      "Secure infrastructure",
      (
        <span key="cicd-k8s">
          <a href="https://www.atlassian.com/continuous-delivery/principles/continuous-integration-vs-delivery-vs-deployment" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">CI/CD</a> &{" "}
          <a href="https://kubernetes.io/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Kubernetes</a>
        </span>
      ),
      (
        <span key="terraform-micro">
          <a href="https://www.terraform.io/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Terraform</a> &{" "}
          <a href="https://aws.amazon.com/microservices/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f7c550] transition-colors">Microservices</a>
        </span>
      )
    ],
    color: "border-l-purple-500",
  },
];

export default function AboutPage() {
  return (
    <div className="overflow-hidden" data-testid="about-page">
      <SEO
        title={"About VANCO | Mission, Vision & Engineering Excellence"}
        description={"Learn about VANCO's mission to build secure, sovereign, and transformational AI solutions for government, fintech, FMCG, and enterprise leaders."}
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="about-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motionFramer.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-[#243b7f]/10 text-[#243b7f] rounded-full text-sm font-medium mb-6">
              About VANCO
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
              Custom AI development from{" "}
              <span className="text-[#243b7f]">concept to production</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              We focus on real, scalable solutions   no experimental pilots, only production-ready systems that drive meaningful business outcomes.
            </p>
          </motionFramer.div>
        </div>
      </section>

      {/* Core Values */}
      <AnimatedSection className="py-24 md:py-32 bg-white" data-testid="core-values">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <FadeIn>
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                Our Foundation
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
                Core Values
              </h2>
              <p className="text-lg text-slate-600">
                The principles that guide everything we do
              </p>
            </FadeIn>
          </div>

          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value) => (
              <AnimatedItem key={value.title}>
                <Card className="h-full bg-white border-slate-100 hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer group overflow-hidden">
                  <CardContent className="p-0 text-left">
                    {/* Header with icon */}
                    <div className={`${value.color} p-5 relative overflow-hidden`}>
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                          <value.icon className={`w-6 h-6 ${value.iconColor}`} />
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#243b7f] group-hover:translate-x-1 transition-all duration-300" />
                      </div>

                      {/* Badge */}
                      <div className="mt-4">
                        <span className={`inline-block px-3 py-1 bg-white/80 rounded-full text-xs font-bold ${value.iconColor} uppercase tracking-wider`}>
                          {value.badgeText}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <p className="text-xs text-slate-500 font-medium mb-2">{value.tagLine}</p>
                      <h3 className="text-lg font-bold text-[#0f172a] font-['Manrope'] mb-2 group-hover:text-[#243b7f] transition-colors leading-tight">
                        {value.title}
                      </h3>
                      <p className="text-slate-600 text-xs leading-relaxed mb-4 min-h-[48px]">
                        {value.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {value.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="bg-slate-50 text-slate-500 text-xs px-2 py-0 border-0">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Client Marquee */}
      <ClientMarquee />

      {/* How We Work */}
      <AnimatedSection className="py-24 md:py-32 bg-white" data-testid="how-we-work">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <FadeIn>
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                Our Process
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
                How We Work
              </h2>
              <p className="text-lg text-slate-600">
                A systematic approach to delivering exceptional results. See how our process powers our <Link to="/expertise" className="text-[#243b7f] font-semibold hover:underline">Deep Tech Expertise</Link>.
              </p>
            </FadeIn>
          </div>

          {/* Process Steps */}
          <div className="relative">
            {/* Connector line - horizontal on desktop */}
            <div className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-slate-200" />

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
              {howWeWork.map((step, index) => (
                <FadeIn key={step.title} delay={index * 0.1}>
                  <div className="text-center">
                    {/* Icon with number */}
                    <div className="relative inline-flex mb-6">
                      <div className="w-20 h-20 rounded-full bg-[#243b7f] flex items-center justify-center shadow-lg shadow-blue-900/20 relative z-10">
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                      <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[#f7c550] flex items-center justify-center text-sm font-bold text-[#0f172a] z-20">
                        {step.step}
                      </span>
                    </div>

                    <h3 className="text-xl font-semibold text-[#0f172a] font-['Manrope'] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Tech Stack */}
      <AnimatedSection className="relative py-24 md:py-32 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="tech-stack">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <FadeIn>
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                Technology
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
                Our Tech Stack
              </h2>
              <p className="text-lg text-slate-600">
                Leveraging the best technologies to build world-class solutions
              </p>
            </FadeIn>
          </div>

          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((tech) => (
              <AnimatedItem key={tech.category}>
                <Card className={`h-full bg-white border-slate-100 border-l-4 ${tech.color} hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <tech.icon className="w-6 h-6 text-[#243b7f]" />
                      <h3 className="text-lg font-semibold text-[#0f172a] font-['Manrope']">
                        {tech.category}
                      </h3>
                    </div>
                    <ul className="space-y-2">
                      {tech.items.map((item) => (
                        <li key={item} className="text-slate-600 text-sm flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#f7c550]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </AnimatedItem>
            ))}
          </AnimatedContainer>

          <FadeIn delay={0.4}>
            <div className="mt-16 text-center">
              <p className="text-lg text-slate-600">
                Ready to leverage our stack? <Link to="/contact" className="text-[#243b7f] font-bold hover:text-[#f7c550] transition-colors hover:underline">Partner with us.</Link>
              </p>
            </div>
          </FadeIn>
        </div>
      </AnimatedSection>
    </div>
  );
}
